import { useEffect } from 'react';
import { connect } from 'redux-bundler-react';
import { useFormContext } from 'react-hook-form';

import { TextInput as UswdsTextInput, Label } from '@trussworks/react-uswds';

import classnames from 'classnames';
import Tooltip from '@components/tooltip/tooltip';
import genericSecondaryModal from '@forms/components/modals/genericSecondaryModal';

import { ErrorMessages } from '@src/utils/enums';
import { sanitizeText, isValidText, isDateValid } from '@src/utils/helpers';
import { decimalNumberRegex } from '@src/utils/regex';
import { useModal } from '@stores';
import FileDownloadList from '../file-download-list/FileDownloadList';

import './TextInput.scss';

const TextInput = connect(
  ({
    defaultValue,
    className = 'width-full',
    fileDownload,
    hint,
    label,
    max,
    min,
    name,
    onBlur = () => {},
    onChange = () => {},
    pattern,
    readOnly,
    required,
    showOptionalText = true,
    signature,
    tooltip,
    tooltipClickable,
    type = 'text',
    maxLength = type === 'text' ? 256 : null,
    uppercase,
    validations,
    valueAsNumber = false,
    ...customProps
  }) => {
    const { openSecondaryModal } = useModal();
    const inputStyles = classnames(className, { 'text-uppercase': uppercase });
    const labelStyles = classnames({
      required: required,
      optional: !required && showOptionalText,
    });

    let endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 100);
    endDate = new Date(endDate).toISOString().slice(0, 10);

    const {
      register,
      watch,
      setValue,
      getValues,
      formState: { errors },
    } = useFormContext();
    const inputError = errors[name];
    const input = watch(name);

    useEffect(() => {
      if (input && typeof input === 'string') {
        if (isValidText(input)) {
          setValue(name, input);
        } else {
          // Optionally handle invalid input here, such as warning the user
          const msg = ErrorMessages.InvalidCharacters;
          openSecondaryModal(genericSecondaryModal, { title: 'Invalid Characters', msg: msg });
          setValue(name, sanitizeText(input));
        }
      }
    }, [input, name, setValue, openSecondaryModal]);

    const handleBlur = (e) => {
      const name = e?.target?.name;
      const value = e?.target?.value;
      onBlur(e);
      // Input type validations
      if (type === 'date') {
        isDateValid(value, min, endDate) === false && setValue(name, '');
      }
      if (type === 'number' || valueAsNumber) {
        if (decimalNumberRegex.test(e?.target?.value) !== true) {
          // Parse out any letters and extra periods from int/float value
          const parsedLettersVal = e?.target?.value?.replace(/[^0-9.]/g, '');
          const rx = /^(\d*\.)([\d.]*)$/;
          const parsedPeriodsVal = parsedLettersVal?.replace(rx, (a, b, c) => b + c?.replace(/\./g, ''));
          setValue(name, parsedPeriodsVal);
        }
      }
    };

    const handleChange = (e) => {
      let value = e.target.value;

      if (type === 'number') {
        if (maxLength && value.length > maxLength) {
          setValue(name, value.slice(0, maxLength));
        } else {
          setValue(name, value);
        }
      }
      onChange(e);
    };

    const inputMax = type === 'date' ? max || endDate : null;
    const inputMin = type === 'date' ? min : null;

    const { ref: textInputRef, ...rest } = register(name, {
      onBlur: handleBlur,
      onChange: handleChange,
      pattern,
      ...validations,
    });

    return (
      <>
        <Label className={labelStyles} htmlFor={name}>
          <span id={`${name}_label`}>{label}</span>
          {tooltip && <Tooltip clickable={tooltipClickable} content={tooltip} header={label} name={name} />}
        </Label>
        {hint && (
          <div className='usa-hint' id={`${name}_hint`}>
            {hint}
          </div>
        )}
        {fileDownload?.length > 0 && <FileDownloadList files={fileDownload} />}
        <UswdsTextInput
          className={inputStyles}
          defaultValue={defaultValue || getValues(name)}
          readOnly={readOnly}
          id={name}
          inputRef={textInputRef}
          max={inputMax}
          maxLength={maxLength}
          min={inputMin}
          name={name}
          required={required}
          type={type}
          validationStatus={inputError && 'error'}
          {...rest}
          {...customProps}
        />
        {signature && <div className='usa-hint margin-bottom-2 padding-left-1 text-italic'>{signature}</div>}
      </>
    );
  }
);

export default TextInput;
